/**
 * Einkauf: aus dem Wochenplan wird eine Liste — minus Vorrat.
 *
 * **Die tragende Regel: nichts stillschweigend weglassen.** Eine Einkaufsliste,
 * auf der eine Zutat fehlt, sieht genauso vollständig aus wie eine richtige; man
 * merkt es erst vor dem leeren Regal zuhause. Deshalb kennt jede Zeile einen von
 * vier Zuständen, und Gerichte ohne Zutatenliste stehen ausdrücklich da, statt
 * zu verschwinden.
 *
 * Gerechnet wird in **Gramm**, weil Zutaten in Gramm gepflegt werden. Was im
 * Vorrat in einer anderen Einheit oder ohne Menge steht, lässt sich nicht
 * abziehen — die Zeile heißt dann „prüfen", nicht „gedeckt".
 */

import type { HouseholdNeeds } from './householdMeals.js';

/**
 * Regalkategorien in der Reihenfolge eines gewöhnlichen Ladengangs: frisch
 * zuerst, Haushalt zuletzt. Die Liste ist danach sortiert, damit man im Laden
 * nicht zurücklaufen muss.
 */
export const SHELF_CATEGORIES = [
  'produce',
  'bakery',
  'chilled',
  'frozen',
  'dry',
  'canned',
  'spices',
  'drinks',
  'household',
  'other',
] as const;

export type ShelfCategory = (typeof SHELF_CATEGORIES)[number];

/** Einheiten einer Menge auf Liste und im Vorrat. Nur `g` lässt sich gegen Zutaten rechnen. */
export const AMOUNT_UNITS = ['g', 'ml', 'piece'] as const;

export type AmountUnit = (typeof AMOUNT_UNITS)[number];

/**
 * Schlüssel, unter dem zwei Einträge als „dasselbe" gelten.
 *
 * Bewusst der **Name**, nicht die Kennung eines Lebensmittels: Lebensmittel
 * gehören einem Konto, Liste und Vorrat dem ganzen Haushalt. Alices
 * „Haferflocken" und Bobs „Haferflocken" sind zwei Zeilen in zwei Konten, aber
 * dieselbe Packung im Schrank.
 */
export function itemKey(name: string): string {
  return name.trim().toLocaleLowerCase('de').replace(/\s+/g, ' ');
}

export function shelfOrder(category: ShelfCategory): number {
  return SHELF_CATEGORIES.indexOf(category);
}

/**
 * Die Zutatenzeile eines Gerichts ohne Zutaten in Einzelteile zerlegen.
 *
 * Der Startbestand schreibt sie als „80 g Haferflocken · 300 ml Sojamilch". Die
 * Teile werden **nicht** gedeutet oder zusammengezählt — „Beeren" hat keine
 * Menge, und eine geratene wäre schlimmer als keine. Sie werden nur einzeln
 * antippbar gemacht.
 */
export function splitItemsText(items: string | null | undefined): string[] {
  if (!items) return [];
  return items
    .split(/\s*[·•;\n]\s*/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

// --- Bedarf aus dem Wochenplan -------------------------------------------------

export interface NeedsPlanEntry {
  date: string;
  recipeId: string | null;
  foodId: string | null;
  portions: number;
  grams: number | null;
}

export interface NeedsRecipe {
  id: string;
  name: string;
  /** Für wie viele Portionen die Zutaten gelten. */
  portions: number;
  items: string | null;
}

export interface NeedsIngredient {
  recipeId: string;
  /** `null` = freie Zeile („1 Prise Salz") — steht mit offener Menge auf der Liste. */
  foodId: string | null;
  grams: number | null;
  label?: string | null | undefined;
}

export interface NeedsFood {
  id: string;
  name: string;
  category: ShelfCategory;
  portionGrams: number | null;
}

/** Ein Eintrag aus Vorrat oder Liste — beide haben dieselbe Form. */
export interface StockEntry {
  name: string;
  amount: number | null;
  unit: AmountUnit | null;
}

export interface NeedsInput {
  plan: readonly NeedsPlanEntry[];
  /**
   * Haushalts-Essensplan (27.10.2026): schon gerechnet über alle Mitesser
   * (`householdNeeds`) — zählt dazu wie der eigene Plan.
   */
  household?: HouseholdNeeds | undefined;
  recipes: readonly NeedsRecipe[];
  ingredients: readonly NeedsIngredient[];
  foods: readonly NeedsFood[];
  pantry: readonly StockEntry[];
  /** Offene, noch nicht abgehakte Einträge der Liste. */
  onList: readonly StockEntry[];
}

/**
 * - `buy` — fehlt, `toBuyG` ist die Menge.
 * - `covered` — der Vorrat reicht.
 * - `listed` — steht schon auf der Liste, in ausreichender oder unbekannter Menge.
 * - `check` — lässt sich nicht rechnen: Menge im Plan unbekannt oder Vorrat
 *   ohne Grammangabe. Die App sagt das, statt zu raten.
 */
export type NeedStatus = 'buy' | 'covered' | 'listed' | 'check';

export interface NeedLine {
  key: string;
  name: string;
  category: ShelfCategory;
  /** `null`, wenn mindestens ein Planeintrag keine Menge hat. */
  neededG: number | null;
  /** Im Vorrat, in Gramm. `null` = nichts Abziehbares gefunden. */
  pantryG: number | null;
  /** Im Vorrat vorhanden, aber ohne Grammangabe — nicht abziehbar. */
  pantryUnknown: boolean;
  /** Schon auf der Liste, in Gramm. */
  listedG: number | null;
  listedUnknown: boolean;
  toBuyG: number | null;
  status: NeedStatus;
  /** Wofür — Gerichtsnamen, ohne Wiederholung. */
  usedIn: string[];
}

export interface UnstructuredRecipe {
  recipeId: string;
  name: string;
  items: string[];
  dates: string[];
}

export interface ShoppingNeeds {
  lines: NeedLine[];
  /** Gerichte ohne Zutatenliste — sie stehen da, statt zu fehlen. */
  unstructured: UnstructuredRecipe[];
  /** Planeinträge, deren Gericht oder Lebensmittel es nicht mehr gibt. */
  missingRefs: number;
}

interface Accumulator {
  name: string;
  category: ShelfCategory;
  grams: number;
  unknownAmount: boolean;
  usedIn: Set<string>;
}

/** Summe abziehbarer Gramm und ob es etwas Unabziehbares gab. */
function stockFor(entries: readonly StockEntry[], key: string) {
  let grams = 0;
  let found = false;
  let unknown = false;
  for (const entry of entries) {
    if (itemKey(entry.name) !== key) continue;
    if (entry.unit === 'g' && entry.amount !== null) {
      grams += entry.amount;
      found = true;
    } else {
      unknown = true;
    }
  }
  return { grams: found ? grams : null, unknown };
}

/**
 * Was der Wochenplan braucht, minus Vorrat und minus dem, was schon auf der
 * Liste steht.
 *
 * Reine Funktion: Die App ruft sie mit den Zeilen aus der lokalen Datenbank auf,
 * und genau so wird sie getestet.
 */
export function buildShoppingNeeds(input: NeedsInput): ShoppingNeeds {
  const recipes = new Map(input.recipes.map((recipe) => [recipe.id, recipe]));
  const foods = new Map(input.foods.map((food) => [food.id, food]));
  const ingredientsByRecipe = new Map<string, NeedsIngredient[]>();
  for (const ingredient of input.ingredients) {
    const list = ingredientsByRecipe.get(ingredient.recipeId) ?? [];
    list.push(ingredient);
    ingredientsByRecipe.set(ingredient.recipeId, list);
  }

  const totals = new Map<string, Accumulator>();
  const unstructured = new Map<string, UnstructuredRecipe>();
  let missingRefs = 0;

  const add = (food: NeedsFood, grams: number | null, usedFor: string) => {
    const key = itemKey(food.name);
    const entry = totals.get(key) ?? {
      name: food.name.trim(),
      category: food.category,
      grams: 0,
      unknownAmount: false,
      usedIn: new Set<string>(),
    };
    if (grams === null) entry.unknownAmount = true;
    else entry.grams += grams;
    entry.usedIn.add(usedFor);
    totals.set(key, entry);
  };

  for (const entry of input.plan) {
    if (entry.recipeId) {
      const recipe = recipes.get(entry.recipeId);
      if (!recipe) {
        missingRefs += 1;
        continue;
      }
      const ingredients = ingredientsByRecipe.get(recipe.id) ?? [];
      if (ingredients.length === 0) {
        const known = unstructured.get(recipe.id) ?? {
          recipeId: recipe.id,
          name: recipe.name,
          items: splitItemsText(recipe.items),
          dates: [],
        };
        if (!known.dates.includes(entry.date)) known.dates.push(entry.date);
        unstructured.set(recipe.id, known);
        continue;
      }
      const factor = recipe.portions > 0 ? entry.portions / recipe.portions : 0;
      for (const ingredient of ingredients) {
        if (ingredient.foodId === null) {
          // Freie Zeile: ohne Lebensmittel kein Regal und keine Menge — sie
          // steht trotzdem da („prüfen"), statt still zu fehlen.
          const label = ingredient.label?.trim();
          if (label) {
            add({ id: '', name: label, category: 'other', portionGrams: null }, null, recipe.name);
          }
          continue;
        }
        const food = foods.get(ingredient.foodId);
        if (!food || ingredient.grams === null) {
          missingRefs += 1;
          continue;
        }
        add(food, ingredient.grams * factor, recipe.name);
      }
      continue;
    }

    if (entry.foodId) {
      const food = foods.get(entry.foodId);
      if (!food) {
        missingRefs += 1;
        continue;
      }
      const grams =
        entry.grams ?? (food.portionGrams !== null ? food.portionGrams * entry.portions : null);
      add(food, grams, food.name.trim());
    }
  }

  // Haushaltsplan (27.10.2026): Zutaten für alle Portionen der Mitesser und
  // was jemand extra braucht — ohne Lebensmittel-Verweis, eingefroren.
  for (const line of input.household?.lines ?? []) {
    add(
      { id: '', name: line.name, category: line.category, portionGrams: null },
      line.grams,
      line.usedFor,
    );
  }
  for (const entry of input.household?.unstructured ?? []) {
    const known = unstructured.get(entry.id) ?? {
      recipeId: entry.id,
      name: entry.name,
      items: splitItemsText(entry.items),
      dates: [],
    };
    if (!known.dates.includes(entry.date)) known.dates.push(entry.date);
    unstructured.set(entry.id, known);
  }

  const lines: NeedLine[] = [...totals.entries()].map(([key, total]) => {
    const pantry = stockFor(input.pantry, key);
    const listed = stockFor(input.onList, key);
    const neededG = total.unknownAmount ? null : Math.ceil(total.grams);

    let toBuyG: number | null = null;
    let status: NeedStatus;

    if (neededG === null) {
      status = listed.grams !== null || listed.unknown ? 'listed' : 'check';
    } else {
      const afterPantry = Math.max(0, neededG - (pantry.grams ?? 0));
      if (afterPantry === 0) {
        status = 'covered';
        toBuyG = 0;
      } else if (listed.unknown || (listed.grams ?? 0) >= afterPantry) {
        status = 'listed';
        toBuyG = 0;
      } else if (pantry.unknown) {
        // Etwas liegt im Schrank, nur weiß niemand wie viel. Kaufen wäre
        // vielleicht doppelt, nicht kaufen vielleicht zu wenig.
        status = 'check';
        toBuyG = afterPantry - (listed.grams ?? 0);
      } else {
        status = 'buy';
        toBuyG = afterPantry - (listed.grams ?? 0);
      }
    }

    return {
      key,
      name: total.name,
      category: total.category,
      neededG,
      pantryG: pantry.grams,
      pantryUnknown: pantry.unknown,
      listedG: listed.grams,
      listedUnknown: listed.unknown,
      toBuyG,
      status,
      usedIn: [...total.usedIn],
    };
  });

  lines.sort(
    (a, b) => shelfOrder(a.category) - shelfOrder(b.category) || a.name.localeCompare(b.name, 'de'),
  );

  return {
    lines,
    unstructured: [...unstructured.values()].map((entry) => ({
      ...entry,
      dates: entry.dates.sort(),
    })),
    missingRefs,
  };
}

// --- Vorrat ------------------------------------------------------------------

/**
 * Eingekauftes in den Vorrat legen: zur vorhandenen Menge addieren, wenn beide
 * dieselbe Einheit tragen — sonst bleibt es ein eigener Eintrag.
 *
 * Gibt die neue Menge zurück oder `null`, wenn nicht zusammengelegt werden kann.
 */
export function mergeIntoStock(
  existing: StockEntry,
  incoming: StockEntry,
): { amount: number; unit: AmountUnit } | null {
  if (itemKey(existing.name) !== itemKey(incoming.name)) return null;
  if (existing.amount === null || incoming.amount === null) return null;
  if (existing.unit === null || existing.unit !== incoming.unit) return null;
  return { amount: existing.amount + incoming.amount, unit: existing.unit };
}
